// src/data/worksheets/injury.js — performance-side pair (authored).
// Built off the Pain & Injury action plan: track it, follow the plan, speak up early.

export const INJURY_CHECKLIST = {
    id: 'injury_checklist', topicId: 'pain-injury', kind: 'checklist',
    title: 'Pain & Injury Checklist',
    intro: 'The aim is to manage it well, not to ignore it. Tick these off as you go.',
    sections: [
        { id: 'early', title: 'Speak Up Early', prompts: [
            { id: 'tell', type: 'checklist', label: 'Tell Someone', items: [
                { id: 't1', label: 'Tell a {physio, doctor, trainer or coach|physio, doctor or occupational health} as soon as pain changes how you move or work — before it becomes a bigger problem.' },
                { id: 't2', label: 'Describe it plainly: where, when, how bad out of ten, what makes it better or worse.' }
            ] },
            { id: 'assess', type: 'checklist', label: 'Get It Assessed', items: [
                { id: 'a1', label: 'Book the assessment rather than waiting to see if it settles.' },
                { id: 'a2', label: 'Ask what you can do, not only what you can\'t.' }
            ] }
        ] },
        { id: 'plan', title: 'Follow the Plan', prompts: [
            { id: 'rehab', type: 'checklist', label: 'Do the Boring Parts', items: [
                { id: 'r1', label: 'If you have rehab or treatment advice, do the boring parts consistently.' },
                { id: 'r2', label: 'Put rehab in a fixed slot in the day like any other session.' },
                { id: 'r3', label: 'Track it — a missed week of rehab is a week added to the timeline.' }
            ] },
            { id: 'load', type: 'checklist', label: 'Respect the Load', items: [
                { id: 'l1', label: 'Follow the return-to-play or return-to-work steps in order, even when you feel good.' },
                { id: 'l2', label: 'Tell the truth about pain during sessions — hiding it lengthens the injury.' }
            ] }
        ] },
        { id: 'track', title: 'Track It', prompts: [
            { id: 'log', type: 'checklist', label: 'Daily Log', items: [
                { id: 'lg1', label: 'Note pain out of ten each day and what you did. Patterns tell you what helps.' },
                { id: 'lg2', label: 'Note sleep and stress too — both change how pain feels.' }
            ] }
        ] },
        { id: 'mind', title: 'The Mental Side', prompts: [
            { id: 'identity', type: 'checklist', label: 'Stay Connected', items: [
                { id: 'i1', label: 'Stay part of the team or group — turn up, contribute in another way.' },
                { id: 'i2', label: 'Keep a routine: wake time, meals, {training-time replaced with rehab or skill work|the time you would normally be active replaced with rehab}.' }
            ] },
            { id: 'thoughts', type: 'checklist', label: 'Watch the Story', items: [
                { id: 'th1', label: 'Catch "I\'ll never get back" and replace it with the next milestone in the plan.' },
                { id: 'th2', label: 'Talk to someone if frustration or low mood is sticking around — injuries hit the head as well as the body.' }
            ] },
            { id: 'use', type: 'checklist', label: 'Use the Time', items: [
                { id: 'u1', label: 'Pick one skill or area you can work on while injured (film, strategy, a course, upper/lower body).' },
                { id: 'u2', label: 'Set a small weekly goal that has nothing to do with the injury.' }
            ] }
        ] },
        { id: 'review', title: 'Review & Reflect', prompts: [
            { id: 'weekly', type: 'checklist', label: 'Weekly', items: [
                { id: 'wk1', label: 'Look at the week\'s pain log. What helped, what flared it?' },
                { id: 'wk2', label: 'Check in with your practitioner on the next step.' }
            ] },
            { id: 'notes', type: 'text', label: 'Notes', multiline: true }
        ] }
    ],
    outro: 'Managed well, an injury is a chapter. Ignored, it becomes the story. Speak up early and do the boring parts.',
    supportNote: 'If the injury is affecting your mood, sleep or how you see yourself, that is worth talking through with someone.'
};

export const INJURY_WORKSHEET = {
    id: 'injury_worksheet', topicId: 'pain-injury', kind: 'worksheet',
    title: 'Pain & Injury Worksheet',
    intro: 'Answer in your own words. Nothing here is scored, and only you can see it.',
    sections: [
        { id: 'self', title: 'Self Assessment', prompts: [
            { id: 'pain', type: 'scale', label: 'Pain today', help: '0 = none, 10 = worst you can imagine.', min: 0, max: 10, minLabel: 'None', maxLabel: 'Worst' },
            { id: 'where', type: 'text', label: 'Where it is and what it feels like', help: 'Sharp, dull, ache, tight. When it started.', multiline: true },
            { id: 'better', type: 'list', label: 'What makes it better', count: 3 },
            { id: 'worse', type: 'list', label: 'What makes it worse', count: 3 },
            { id: 'impact', type: 'text', label: 'What it is stopping you doing', multiline: true }
        ] },
        { id: 'understanding', title: 'Understanding It', prompts: [
            { id: 'told', type: 'text', label: 'Who knows about it, and what have they said?', help: '{Physio, doctor, coach, trainer, manager|Physio, doctor, occupational health, manager}.', multiline: true },
            { id: 'plan', type: 'text', label: 'The plan as you understand it', help: 'Treatment, rehab, timeline, next milestone. If you don\'t know — that is the first question to ask.', multiline: true },
            { id: 'feelings', type: 'list', label: 'How it is affecting you {off the field / off the job|off the job}', help: 'Mood, sleep, patience, how you see yourself.', count: 3 }
        ] },
        { id: 'building', title: 'Managing It', prompts: [
            { id: 'rehabslot', type: 'text', label: 'Your fixed rehab slot', help: 'When, where, how long.' },
            { id: 'milestones', type: 'list', label: 'The next three milestones in the plan', count: 3, itemLabels: ['Milestone 1', 'Milestone 2', 'Milestone 3'] },
            { id: 'cando', type: 'list', label: 'What you CAN do right now', help: '{Training you can still do|Activity you can still do}, skills to work on, ways to contribute.', count: 3 },
            { id: 'story', type: 'text', label: 'The unhelpful story — and the replacement', help: 'e.g., "I\'ll never get back" → "next milestone is full-speed running in two weeks".', multiline: true }
        ] },
        { id: 'plan', title: 'Action Plan', prompts: [
            { id: 'goal', type: 'text', label: 'This week\'s rehab goal', help: 'e.g., "every session, all reps".' },
            { id: 'othergoal', type: 'text', label: 'One goal for the week that has nothing to do with the injury' },
            { id: 'people', type: 'list', label: 'Who you will check in with', help: '{Practitioner, coach, someone for the head side|Practitioner, manager, someone for the head side}.', count: 2 }
        ] },
        { id: 'track', title: 'Track It', description: 'Pain, rehab done, activity, sleep. The chart makes flare-ups and progress obvious.', prompts: [
            { id: 'tracker', type: 'table', label: 'Daily pain & rehab log', columns: [
                { id: 'day', label: 'Day', kind: 'date' },
                { id: 'pain', label: 'Pain (0–10)', kind: 'number', min: 0, max: 10 },
                { id: 'rehab', label: 'Rehab done (1 = yes, 0 = no)', kind: 'number', min: 0, max: 1 },
                { id: 'activity', label: 'Activity / load', kind: 'text' },
                { id: 'sleep', label: 'Sleep (1–10)', kind: 'number', min: 1, max: 10 },
                { id: 'notes', label: 'Notes', kind: 'text' }
            ], maxRows: 31, chart: { x: 'day', series: [{ id: 'pain', label: 'Pain' }, { id: 'sleep', label: 'Sleep' }, { id: 'rehab', label: 'Rehab' }] } }
        ] }
    ],
    outro: 'Track it, follow the plan, speak up early. The body heals faster when the head is on side.',
    supportNote: 'If the injury is affecting your mood, sleep or how you see yourself, that is worth talking through with someone.'
};

export const INJURY_WORKSHEETS = [INJURY_CHECKLIST, INJURY_WORKSHEET];
